// Stage 50: Gadfly Module — Settings tab.
//
// Operator-only. Renders:
//   - Enable switch + short explainer.
//   - Healthcheck banner.
//   - Threshold tuners (model, temperature, stripe keep/cut, timeout, etc.).
//   - "Test bite" affordance (POST /api/gadfly/bite from a textarea).
//   - The live current-turn Gadfly inspector pane.
//
// NO history, NO regenerate, NO suppress, NO export — Gadfly is stateless
// and memoryless by design (TINS §Implementation Notes #1, #8).

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  gadflyHealthcheck,
  gadflyBite,
  type GadflyHealth,
  type BiteResponse,
} from '../api/gadfly';
import { GadflyInspector } from './GadflyInspector';

interface SettingsResponse {
  settings: Record<string, string>;
}

const API_BASE = import.meta.env.VITE_API_BASE as string;
void API_BASE; // referenced for parity with sibling tabs

const THRESHOLD_KEYS = [
  ['gadfly_model', 'model', 'gemini-3-flash-preview', 'text'],
  ['gadfly_temperature', 'temperature (soft-capped at 1.5)', '1.3', 'number'],
  ['gadfly_stripe_keep', 'stripe keep (lines kept per period)', '1', 'number'],
  ['gadfly_stripe_cut', 'stripe cut (lines dropped per period)', '2', 'number'],
  ['gadfly_max_output_tokens', 'max output tokens', '800', 'number'],
  ['gadfly_timeout_ms', 'timeout (ms)', '8000', 'number'],
  ['gadfly_max_bites', 'max bites per invocation', '20', 'number'],
  ['gadfly_min_input_lines', 'min input lines before stripe', '3', 'number'],
  ['gadfly_max_context_chars', 'max context chars (truncated from left)', '200000', 'number'],
] as const;

export function GadflyTab({
  onChanged,
  currentConversationId,
}: {
  onChanged: () => void;
  currentConversationId?: string | null;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [health, setHealth] = useState<GadflyHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Test bite affordance
  const [testContext, setTestContext] = useState<string>(
    'USER: Can you remind me what the deadline was for the benchmarks?\nASSISTANT: I think we agreed Friday but let me check the bulletin.\nUSER: Yeah Friday — also which suite are we targeting?',
  );
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<BiteResponse | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);

  // Load current admin_settings on mount.
  const loadAll = useCallback(async () => {
    setErr(null);
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings ?? {};
      setSettings(s);
      setEnabled(s.gadfly_enabled === 'true');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load settings');
    }
    try {
      const h = await gadflyHealthcheck();
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(e instanceof Error ? e.message : 'healthcheck failed');
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const writeSetting = useCallback(
    async (key: string, value: string) => {
      setErr(null);
      setInfo(null);
      try {
        await apiJson('PUT', '/api/settings', { [key]: value });
        setSettings((prev) => ({ ...prev, [key]: value }));
        onChanged();
      } catch (e) {
        setErr(e instanceof Error ? e.message : `failed to write ${key}`);
      }
    },
    [onChanged],
  );

  const toggleEnabled = useCallback(async () => {
    if (enabled === null) return;
    setBusy(true);
    const next = !enabled;
    await writeSetting('gadfly_enabled', next ? 'true' : 'false');
    setEnabled(next);
    setInfo(next ? 'Gadfly enabled.' : 'Gadfly disabled.');
    setBusy(false);
    // Refresh healthcheck so the banner reflects the new flag value.
    try {
      const h = await gadflyHealthcheck();
      setHealth(h);
    } catch {
      /* ignore */
    }
  }, [enabled, writeSetting]);

  const runTestBite = useCallback(async () => {
    setTestBusy(true);
    setTestErr(null);
    setTestResult(null);
    try {
      const res = await gadflyBite({
        context: testContext,
        conversationId: currentConversationId ?? `test-${Date.now()}`,
        turnId: `test-${Date.now()}`,
      });
      setTestResult(res);
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : 'bite failed');
    } finally {
      setTestBusy(false);
    }
  }, [testContext, currentConversationId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h3 style={{ margin: 0 }}>Gadfly</h3>
        <p
          style={{
            margin: '6px 0 0 0',
            color: 'var(--muted)',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          A small confused observer that fires once per assistant turn at
          high temperature against a striped (1-of-3) copy of the
          conversation. Its <em>bites</em> are injected into the system
          prompt as a <code>GADFLY CONFUSION</code> block. Most are
          misreadings the persona will (and should) ignore. Occasionally
          one catches something real — the persona can mark it via
          <code> gadfly_swat</code>. Gadfly is stateless and memoryless:
          there is no history, no accumulation, no persisted side effects.
        </p>
      </header>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            onChange={toggleEnabled}
            disabled={enabled === null || busy}
          />
          <strong>Enable Gadfly</strong>
        </label>
        {health ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
            Healthcheck: <code>{health.service}</code> · model{' '}
            <code>{health.model}</code> · API key{' '}
            {health.apiKeyConfigured ? '✓ configured' : '⚠ not set'} ·
            region <code>{health.region}</code>
          </div>
        ) : healthErr ? (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
            Healthcheck failed: {healthErr}
          </div>
        ) : null}
      </section>

      {err ? (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>
      ) : null}
      {info ? (
        <div style={{ color: 'var(--ok)', fontSize: 13 }}>{info}</div>
      ) : null}

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Thresholds</h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1fr) 160px',
            gap: 8,
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          {THRESHOLD_KEYS.map(([key, label, fallback, kind]) => (
            <ThresholdRow
              key={key}
              k={key}
              label={label}
              value={settings[key] ?? fallback}
              kind={kind}
              onWrite={(v) => writeSetting(key, v)}
            />
          ))}
        </div>
      </section>

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Test bite</h4>
        <textarea
          value={testContext}
          onChange={(e) => setTestContext(e.target.value)}
          rows={6}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: 12,
            padding: 8,
          }}
          aria-label="Test context for the Gadfly bite"
        />
        <button
          onClick={runTestBite}
          disabled={testBusy || enabled !== true}
          style={{
            marginTop: 8,
            padding: '6px 14px',
            background:
              enabled === true ? 'var(--warning)' : 'var(--panel-2)',
            color: enabled === true ? '#0a0c10' : 'var(--muted)',
            border: 'none',
            borderRadius: 6,
            cursor: enabled === true ? 'pointer' : 'not-allowed',
            fontWeight: 600,
          }}
        >
          {testBusy ? 'biting...' : 'Test bite'}
        </button>
        {testErr ? (
          <div style={{ marginTop: 8, color: 'var(--danger)', fontSize: 13 }}>
            {testErr}
          </div>
        ) : null}
        {testResult ? (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              background: 'var(--panel)',
              border: '1px solid var(--border-soft)',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            <div>
              <strong>Bites:</strong> {testResult.bites.length}
              {' · '}
              <strong>Model:</strong> <code>{testResult.modelUsed}</code>
              {' · '}
              <strong>Duration:</strong> {testResult.durationMs}ms
              {' · '}
              <strong>Stripe:</strong>{' '}
              {testResult.stripeRatioApplied.keep}-of-
              {testResult.stripeRatioApplied.keep +
                testResult.stripeRatioApplied.cut}
              {testResult.failed ? (
                <span style={{ color: 'var(--danger)' }}>
                  {' '}
                  · failed: {testResult.failureReason ?? 'unknown'}
                </span>
              ) : null}
            </div>
            {testResult.bites.length > 0 ? (
              <ol style={{ marginTop: 6, paddingLeft: 20 }}>
                {testResult.bites.map((b) => (
                  <li key={b.index}>
                    <code>[{b.kind}]</code> {b.text}
                  </li>
                ))}
              </ol>
            ) : null}
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>
                Raw confusion.md (operator only — never shown to end users)
              </summary>
              <pre
                style={{
                  marginTop: 4,
                  padding: 6,
                  background: 'var(--bg)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 240,
                  overflow: 'auto',
                  fontSize: 11,
                  color: 'var(--fg)',
                }}
              >
                {testResult.rawConfusionMd || '(empty)'}
              </pre>
            </details>
          </div>
        ) : null}
      </section>

      <section>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
          Live current turn
        </h4>
        <GadflyInspector />
      </section>
    </div>
  );
}

function ThresholdRow({
  k,
  label,
  value,
  kind,
  onWrite,
}: {
  k: string;
  label: string;
  value: string;
  kind: 'text' | 'number';
  onWrite: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);
  return (
    <>
      <label htmlFor={`gadfly-${k}`} style={{ color: 'var(--muted)' }}>
        {label}
      </label>
      <input
        id={`gadfly-${k}`}
        type={kind}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== value) onWrite(local);
        }}
        style={{
          padding: '4px 8px',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      />
    </>
  );
}
